import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Container, Alert, Row, Col } from 'react-bootstrap';
import {
  HiArrowLeft,
  HiOutlineClipboardCopy,
  HiCheck,
  HiExternalLink,
  HiShieldCheck,
  HiSparkles,
  HiShare,
  FaWhatsapp,
  FaTwitter,
  FaFacebookF,
  FaEnvelope,
  FaLink
} from './components/Icons';
import SEO from './components/SEO';
import CommunityVote from './components/CommunityVote';
import AmexPointsCalculator from './components/AmexPointsCalculator';
import companiesData from './companies.json';
import { slugifyCategory } from './utils/categories';
import { trackOutboundClick, trackCouponCopy, trackSocialShare } from './utils/analytics';
import { useLanguage } from './context/LanguageContext';
import './Company.css';
import { CompanyType } from './types';
import ReactGA from 'react-ga4';

const companies: CompanyType[] = companiesData;

function Company() {
  const { t } = useLanguage();
  const { companyId } = useParams<{ companyId: string }>();
  const [couponCode, setCouponCode] = useState<string | undefined>('');
  const [couponLink, setCouponLink] = useState<string | undefined>('');
  const [couponValue, setCouponValue] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyLink, setCompanyLink] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string | undefined>('');
  const [brandColor, setBrandColor] = useState<string | undefined>('#4f46e5');
  const [copied, setCopied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch (e) {
        // Safe fallback in test environment
      }
    }
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
    
    const company: CompanyType | undefined = companies.find(
      c => c.name.replace(/\s+/g, '_').toLowerCase() === companyId
    );

    if (company) {
      setCompanyName(company.name);
      setCompanyLink(company.link);
      setCouponCode(company.coupon.code);
      setCouponLink(company.coupon.link);
      setCouponValue(company.coupon.value);
      setDescription(company.coupon.description);
      setCategory(company.category);
      setBrandColor(company.color || '#4f46e5');
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [companyId]);

  const copyToClipboard = (text: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }).catch(() => {
        // Fallback
      });
    }
  };

  const handleCopy = () => {
    if (couponCode) {
      trackCouponCopy(companyName, couponCode, category);
      copyToClipboard(couponCode);
    }
  };

  const handleFacebookShare = () => {
    trackSocialShare('facebook', companyName);
    const url = window.location.href;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank');
  };
  
  const handleTwitterShare = () => {
    trackSocialShare('twitter', companyName);
    const url = window.location.href;
    const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=Get ${couponValue} at ${companyName}!`;
    window.open(shareUrl, '_blank');
  };
  
  const handleWhatsAppShare = () => {
    trackSocialShare('whatsapp', companyName);
    const url = window.location.href;
    const shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent('Get ' + couponValue + ' at ' + companyName + '! ' + url)}`;
    window.open(shareUrl, '_blank');
  };
  
  const handleSmsShare = () => {
    trackSocialShare('sms', companyName);
    const url = window.location.href;
    const body = `Get ${couponValue} at ${companyName}! ${url}`;
    window.open(`sms:?&body=${encodeURIComponent(body)}`, '_self');
  };

  const handleCopyLink = () => {
    trackSocialShare('copy_link', companyName);
    const url = window.location.href;
    copyToClipboard(`Get ${couponValue} at ${companyName}! ${url}`);
  };

  const handleRedirect = () => {
    if (couponLink) {
      trackOutboundClick({
        partnerName: companyName,
        category: category || 'General',
        dealValue: couponValue,
        linkUrl: couponLink
      });
      window.location.href = couponLink;
    }
  };

  // Get initials for brand avatar
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Related deals
  const relatedDeals = companies
    .filter(c => c.name.replace(/\s+/g, '_').toLowerCase() !== companyId)
    .slice(0, 3);

  const structuredData = React.useMemo(() => {
    if (!companyName) return undefined;
    const companySlug = companyName.replace(/\s+/g, '_').toLowerCase();
    const pageUrl = `https://couponshowcase.com/${companySlug}`;
    const categorySlug = category ? slugifyCategory(category) : '';
    const categoryUrl = categorySlug ? `https://couponshowcase.com/kategorie/${categorySlug}` : 'https://couponshowcase.com/#categories';

    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Startseite",
            "item": "https://couponshowcase.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": category || "Deals",
            "item": categoryUrl
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${companyName} Gutschein`,
            "item": pageUrl
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "Offer",
        "@id": `${pageUrl}#offer`,
        "name": `${companyName} Gutschein - ${couponValue}`,
        "description": description,
        "price": "0",
        "priceCurrency": "EUR",
        "category": category || "Gutscheine & Rabatte",
        "url": pageUrl,
        "seller": {
          "@type": "Organization",
          "name": companyName,
          "url": companyLink
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": `Wie löse ich den ${companyName} Gutschein ein?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": couponCode
                ? `Kopiere den Gutscheincode '${couponCode}' und gib ihn beim Bestellvorgang bei ${companyName} ein.`
                : `Klicke auf 'Angebot einlösen', um direkt über den offiziellen Einladungslink den ${couponValue} Vorteil bei ${companyName} zu erhalten.`
            }
          },
          {
            "@type": "Question",
            "name": `Ist der ${companyName} Vorteil kostenlos?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `Ja, alle auf Coupon Showcase hinterlegten Rabattaktionen und Codes für ${companyName} sind zu 100% kostenlos und sofort abrufbar.`
            }
          }
        ]
      }
    ];
  }, [companyName, couponCode, couponValue, description, category, companyLink]);

  if (notFound) {
    return (
      <div className="company-page py-5">
        <SEO
          title={`${t('notFoundTitle', 'Gutschein nicht gefunden')} | Coupon Showcase`}
          description={t('notFoundSubtitle', 'Der gesuchte Gutschein konnte leider nicht gefunden werden oder ist nicht mehr verfügbar.')}
          canonical="/"
        />
        <Container>
          <div className="empty-detail-card text-center p-5 mx-auto">
            <div className="empty-icon mb-3">🔍</div>
            <h2 className="mb-2">{t('notFoundTitle', 'Gutschein nicht gefunden')}</h2>
            <p className="text-muted mb-4">
              {t('notFoundSubtitle', 'Der gesuchte Gutschein konnte leider nicht gefunden werden oder ist nicht mehr verfügbar.')}
            </p>
            <Link to="/" className="btn btn-primary px-4 py-2">
              <HiArrowLeft className="me-1" /> {t('backToOverview', 'Zurück zur Übersicht')}
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="company-page py-4">
      <SEO
        title={`${companyName} Gutschein & Rabattcode (${couponValue}) | Coupon Showcase 2026`}
        description={`${companyName} Gutschein: ${description} Jetzt ${couponValue} Vorteil sichern & sparen.`}
        canonical={`/${companyId}`}
        keywords={`${companyName} Gutschein, ${companyName} Rabattcode, ${companyName} Coupon, ${companyName} Angebot 2026, ${companyName} Prämie, ${companyName} Rabatt`}
        structuredData={structuredData}
      />

      <Container>
        {/* Navigation Breadcrumb & Back button */}
        <div className="page-nav-bar d-flex justify-content-between align-items-center mb-4">
          <Link to="/" className="back-link">
            <HiArrowLeft className="me-1" /> {t('backToOverview', 'Zurück zur Übersicht')}
          </Link>
          <div className="breadcrumb-pill">
            <Link to="/">Startseite</Link>
            <span className="mx-2">/</span>
            {category ? (
              <Link to={`/kategorie/${slugifyCategory(category)}`}>{category}</Link>
            ) : (
              <span>Deals</span>
            )}
            <span className="mx-2">/</span>
            <strong className="text-dark">{companyName}</strong>
          </div>
        </div>

        <Row className="justify-content-center">
          <Col lg={9} xl={8}>
            {/* Main Voucher Hero Card */}
            <div className="detail-hero-card">
              {/* Card Header Profile */}
              <div className="detail-header-row">
                <div className="brand-header-left">
                  <div className="detail-brand-avatar" style={{ background: brandColor }}>
                    {getInitials(companyName)}
                  </div>
                  <div>
                    <div className="brand-badge-row">
                      <span className="verified-badge">
                        <HiShieldCheck className="me-1" /> Geprüft & Gültig 2026
                      </span>
                      {category && (
                        <Link to={`/kategorie/${slugifyCategory(category)}`} className="cat-pill">
                          {category}
                        </Link>
                      )}
                    </div>
                    <h1 className="detail-main-title">
                      Get {couponValue} at{' '}
                      <a href={companyLink} target="_blank" rel="noreferrer" className="company-link-title">
                        {companyName} <HiExternalLink className="ext-icon" />
                      </a>
                      !
                    </h1>
                  </div>
                </div>
              </div>

              {/* Central Interactive Coupon Action Box */}
              <div className="coupon-action-box my-4">
                <div className="value-banner">
                  <span className="value-banner-label">Dein Sparvorteil</span>
                  <span className="value-banner-amount">{couponValue}</span>
                </div>

                {couponCode && (
                  <div className="code-redemption-area">
                    <div className="code-box-label">Gutscheincode beim Checkout einlösen:</div>
                    <div className="code-display-wrapper">
                      <div className="coupon-code-string">{couponCode}</div>
                      <Button
                        className="modern-copy-btn"
                        onClick={handleCopy}
                        aria-label="Gutscheincode kopieren"
                      >
                        {copied ? (
                          <>
                            <HiCheck className="me-1" /> Kopiert!
                          </>
                        ) : (
                          <>
                            <HiOutlineClipboardCopy className="me-1" /> Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {couponLink && (
                  <div className="link-redemption-area">
                    <p className="link-redemption-hint">
                      Kein Gutscheincode nötig – der Rabatt wird über den Einladungslink automatisch aktiviert!
                    </p>
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      <Button
                        className="modern-go-btn"
                        onClick={handleRedirect}
                      >
                        <HiSparkles className="me-1" /> Go (Angebot öffnen)
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="modern-copy-link-btn"
                        onClick={handleCopyLink}
                      >
                        <FaLink className="me-1" /> Link kopieren
                      </Button>
                    </div>
                  </div>
                )}

                {copied && (
                  <Alert variant="success" className="copied-toast-alert mt-3 text-center">
                    <HiCheck className="me-1 text-success" /> Erfolgreich in die Zwischenablage kopiert!
                  </Alert>
                )}
              </div>

              {/* Social Proof: Community Verification */}
              <CommunityVote companyId={companyId || ''} companyName={companyName} />

              {/* Description & Terms */}
              <div className="detail-description-section mb-4">
                <h3 className="section-subtitle">Details zum Angebot</h3>
                <p className="description-text">{description}</p>
              </div>

              {/* Interactive Amex Points Value Calculator */}
              {companyId === 'american_express_platinum' && (
                <AmexPointsCalculator referralLink={couponLink} initialPoints={85000} />
              )}

              {/* Step-by-Step Instructions */}
              <div className="redemption-steps-section mb-4">
                <h3 className="section-subtitle">So löst du den Vorteil ein:</h3>
                <div className="step-row">
                  <div className="step-badge">1</div>
                  <div className="step-text">
                    {couponCode ? 'Kopiere den Gutscheincode oben.' : 'Klicke auf "Go (Angebot öffnen)", um zur Partnerseite zu gelangen.'}
                  </div>
                </div>
                <div className="step-row">
                  <div className="step-badge">2</div>
                  <div className="step-text">
                    Registriere dich bei <strong>{companyName}</strong> oder lege deine Wunschartikel in den Warenkorb.
                  </div>
                </div>
                <div className="step-row">
                  <div className="step-badge">3</div>
                  <div className="step-text">
                    {couponCode ? 'Füge den Code im Gutscheinfeld ein.' : 'Dein Vorteil wird automatisch berücksichtigt.'} Viel Spaß beim Sparen!
                  </div>
                </div>
              </div>

              {/* Social Share Bar */}
              <div className="detail-share-bar pt-4 border-top">
                <div className="share-title mb-3">
                  <HiShare className="me-1 text-primary" /> Diesen Deal mit Freunden teilen:
                </div>
                <div className="share-buttons-grid">
                  <button className="share-btn whatsapp" onClick={handleWhatsAppShare} aria-label="WhatsApp">
                    <FaWhatsapp className="share-btn-icon" /> WhatsApp
                  </button>
                  <button className="share-btn twitter" onClick={handleTwitterShare} aria-label="X / Twitter">
                    <FaTwitter className="share-btn-icon" /> X (Twitter)
                  </button>
                  <button className="share-btn facebook" onClick={handleFacebookShare} aria-label="Facebook">
                    <FaFacebookF className="share-btn-icon" /> Facebook
                  </button>
                  <button className="share-btn email" onClick={handleSmsShare} aria-label="SMS / Email">
                    <FaEnvelope className="share-btn-icon" /> Nachricht
                  </button>
                  <button className="share-btn copylink" onClick={handleCopyLink} aria-label="Copy Link">
                    <FaLink className="share-btn-icon" /> Copy Link
                  </button>
                </div>
              </div>
            </div>

            {/* Related Deals Section */}
            <div className="related-deals-section mt-5">
              <h3 className="section-heading mb-3">Weitere Top-Gutscheine entdecken</h3>
              <Row className="g-3">
                {relatedDeals.map((deal, idx) => {
                  const dealSlug = deal.name.replace(/\s+/g, '_').toLowerCase();
                  return (
                    <Col md={4} key={idx}>
                      <div className="related-deal-card">
                        <div className="related-avatar" style={{ background: deal.color || '#4f46e5' }}>
                          {getInitials(deal.name)}
                        </div>
                        <h4 className="related-name">{deal.name}</h4>
                        <div className="related-value">{deal.coupon.value}</div>
                        <Link to={`/${dealSlug}`} className="stretched-link" />
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Company;
